import {
  runEventSchema,
  type ProblemDetails,
  type RunEventDto,
  workflowEventSchema,
  type WorkflowEventDto,
} from '@agent-platform/shared';
import type { z } from 'zod';
import { ApiError, mutationHeaders } from '@/lib/api';

/**
 * POSTs to an SSE endpoint and calls `onEvent` for each event. Resolves when the stream ends;
 * request errors (sent as problem+json before the stream opens) reject with an `ApiError`.
 */
export function postEventStream(
  path: string,
  body: unknown,
  onEvent: (event: RunEventDto) => void,
  signal: AbortSignal,
): Promise<void> {
  return postStream(path, body, runEventSchema, onEvent, signal);
}

/** Streams a workflow run (see `workflowEventSchema`). */
export function postWorkflowStream(
  path: string,
  body: unknown,
  onEvent: (event: WorkflowEventDto) => void,
  signal: AbortSignal,
): Promise<void> {
  return postStream(path, body, workflowEventSchema, onEvent, signal);
}

/** Live events of a workflow run started elsewhere (ends at once if it is not running). */
export function getWorkflowStream(
  path: string,
  onEvent: (event: WorkflowEventDto) => void,
  signal: AbortSignal,
): Promise<void> {
  return postStream(path, undefined, workflowEventSchema, onEvent, signal, 'GET');
}

async function postStream<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
  onEvent: (event: z.infer<S>) => void,
  signal: AbortSignal,
  method: 'GET' | 'POST' = 'POST',
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`/api/admin${path}`, {
      method,
      headers:
        method === 'POST'
          ? {
              accept: 'text/event-stream',
              'content-type': 'application/json',
              ...mutationHeaders(),
            }
          : { accept: 'text/event-stream' },
      credentials: 'same-origin',
      ...(method === 'POST' ? { body: JSON.stringify(body ?? {}) } : {}),
      signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') return;
    throw new ApiError(0, {
      detail: 'Cannot reach the server. Check your connection and try again.',
    });
  }
  if (!response.ok || !response.body) {
    const problem = (await response.json().catch(() => null)) as Partial<ProblemDetails> | null;
    throw new ApiError(response.status, problem);
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;
      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const data = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n');
        if (!data) continue; // heartbeat comment
        const parsed = schema.safeParse(JSON.parse(data));
        if (parsed.success) onEvent(parsed.data);
      }
    }
  } catch (error) {
    if ((error as Error).name !== 'AbortError') throw error;
  }
}
